import { Spinner } from './feedback';
import { useState } from 'react';
import {
  MODPACK_ARCHIVE_LIMIT_LABEL,
  MODPACK_LIMITS,
  type ModpackPreview,
  type ModSearchHit,
  type ModrinthClient,
} from '@mcpanel/core';
import { api } from '../api/client';
import { AddonSearch } from './AddonSearch';
import { errorMessage } from '../app-context';

type Versions = Awaited<ReturnType<ModrinthClient['modpackVersions']>>;
export function ModpackPicker({
  onSelect,
  onPending,
}: {
  onSelect: (pack: ModpackPreview) => void;
  onPending?: () => void;
}) {
  const [pending, setPending] = useState<ModpackPreview>();
  const accept = (pack: ModpackPreview) => {
    setPending(pack.missing?.length ? pack : undefined);
    if (pack.missing?.length) onPending?.();
    if (!pack.missing?.length) onSelect(pack);
  };
  const [project, setProject] = useState<ModSearchHit>();
  const [versions, setVersions] = useState<Versions>([]);
  const [versionId, setVersionId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  const choose = (hit: ModSearchHit) =>
    void run(async () => {
      setProject(hit);
      setVersions([]);
      setVersionId('');
      const list =
        hit.source === 'curseforge'
          ? await api.modpackVersions(hit.projectId, hit.source)
          : await api.modpackVersions(hit.projectId);
      setVersions(list);
      setVersionId(list.find((version) => version.type === 'release')?.id ?? list[0]?.id ?? '');
    });
  return (
    <section className="stack">
      <AddonSearch
        modpack
        family=""
        mcVersion=""
        sources={['modrinth', 'curseforge']}
        stateOf={() => (busy ? 'staged' : undefined)}
        onAdd={choose}
        title="Modpack を探す"
      />
      {error && (
        <p role="alert" className="alert danger">
          {error}
        </p>
      )}
      {project && (
        <div className="card card-body stack">
          <h3>{project.title} の版</h3>
          <label>
            版・Minecraft・ローダー・公開日
            <select
              className="input"
              aria-label="Modpack の版"
              value={versionId}
              disabled={busy}
              onChange={(event) => setVersionId(event.target.value)}
            >
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.number} ({version.type}) / {version.gameVersions.join(', ')} / {version.loaders.join(', ')} /{' '}
                  {new Date(version.date).toLocaleDateString('ja-JP')}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn primary"
            disabled={busy || !versionId}
            onClick={() =>
              void run(async () =>
                accept(
                  project.source === 'curseforge'
                    ? await api.selectModpack(project.projectId, versionId, project.source)
                    : await api.selectModpack(project.projectId, versionId),
                ),
              )
            }
          >
            {busy && <Spinner />}
            {busy ? '内容を検証中…' : 'この Modpack を選ぶ'}
          </button>
        </div>
      )}
      {pending && (
        <MissingPackMods
          pack={pending}
          busy={busy}
          onUpload={(mod, file) => run(async () => accept(await api.uploadModpackJar(pending.id, mod, file)))}
        />
      )}
      <label className="card card-body stack">
        .mrpack をアップロード / CurseForge .zip（{MODPACK_ARCHIVE_LIMIT_LABEL} まで）
        <input
          type="file"
          accept=".mrpack,.zip"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file)
              void run(async () => {
                if (file.size > MODPACK_LIMITS.archiveBytes)
                  throw new Error(`アップロードは ${MODPACK_ARCHIVE_LIMIT_LABEL} までです`);
                accept(await api.uploadModpack(file));
              });
            event.target.value = '';
          }}
        />
      </label>
    </section>
  );
}

async function sha1Hex(file: File) {
  const digest = await crypto.subtle.digest('SHA-1', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function MissingPackMods({
  pack,
  busy,
  onUpload,
}: {
  pack: ModpackPreview;
  busy: boolean;
  onUpload: (projectId: string, file: File) => Promise<unknown>;
}) {
  const [message, setMessage] = useState('');
  const [dragging, setDragging] = useState(false);
  // ブラウザはダウンロードフォルダを勝手に読めないので、落としたファイルをまとめて選ぶ・ドロップする。
  // どのファイルがどの Mod かは、サイズと sha1 で自動で振り分ける
  const importFiles = async (files: File[]) => {
    const missing = pack.missing ?? [];
    const matched: string[] = [];
    const unmatched: string[] = [];
    for (const file of files) {
      const candidates = missing.filter((mod) => mod.file.size === file.size && !matched.includes(mod.projectId));
      const hash = candidates.length ? await sha1Hex(file) : '';
      const mod = candidates.find((entry) => entry.file.sha1 === hash);
      if (!mod) {
        unmatched.push(file.name);
        continue;
      }
      await onUpload(mod.projectId, file);
      matched.push(mod.projectId);
    }
    setMessage(
      `${matched.length} 個を入れました。` +
        (unmatched.length ? ` 一致しなかったファイル（別の版など）: ${unmatched.join('、')}` : ''),
    );
  };
  return (
    <div className="card card-body stack">
      <p>手動アップロードがあと {pack.missing?.length} 個必要です。そろうまで作成・導入を開始できません。</p>
      <p className="small muted">
        各 Mod の名前のリンクから CurseForge のページを開いてダウンロードし、落としたファイルをまとめて下に選ぶか、
        ここにドラッグ＆ドロップしてください。どれがどの Mod かは自動で振り分けます。
      </p>
      <label
        className={`card card-body stack${dragging ? ' active' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void importFiles(Array.from(event.dataTransfer.files));
        }}
      >
        ダウンロードした jar をまとめて選ぶ（ドラッグ＆ドロップもできます）
        <input
          type="file"
          accept=".jar"
          multiple
          disabled={busy}
          aria-label="ダウンロードした jar をまとめて選ぶ"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            void importFiles(files);
          }}
        />
      </label>
      {message && (
        <p className="small" role="status">
          {message}
        </p>
      )}
      {pack.missing?.map((mod) => (
        <label key={mod.projectId}>
          <a href={mod.manual!.pageUrl} target="_blank" rel="noreferrer">
            {mod.title}
          </a>{' '}
          — {mod.file.filename}
          <input
            type="file"
            accept=".jar"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void onUpload(mod.projectId, file);
              event.target.value = '';
            }}
          />
        </label>
      ))}
    </div>
  );
}
